import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AuditOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { analysisApi, ProposalAnalysisDto, SuggestionItem } from '@/api/analysisApi';
import { usePolling } from '@/hooks/usePolling';

const { Text, Title, Paragraph } = Typography;

interface Props {
  opportunityId: string;
}

const POLL_INTERVAL = 3000;

const scoreColor = (score: number | undefined): string => {
  if (score === undefined || score === null) return '#d9d9d9';
  if (score >= 80) return '#52c41a';
  if (score >= 60) return '#faad14';
  return '#ff4d4f';
};

const severityColor: Record<string, string> = {
  high: '#ff4d4f',
  medium: '#faad14',
  low: '#52c41a',
};

const dimensionLabels: Record<string, string> = {
  compliance: 'Compliance',
  keywords: 'Keywords',
  technical_terms: 'Technical Terms',
  grammar: 'Grammar',
  punctuation: 'Punctuation',
  structure: 'Structure',
  flow: 'Flow',
  word_count: 'Word Count',
  candidate_fit: 'Candidate Fit',
  selection_probability: 'Win Probability',
};

const ProposalAIReview: React.FC<Props> = ({ opportunityId }) => {
  const [analysis, setAnalysis] = useState<ProposalAnalysisDto | null>(null);
  const [allAnalyses, setAllAnalyses] = useState<ProposalAnalysisDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await analysisApi.getLatest(opportunityId);
      if (res.status === 204) {
        setAnalysis(null);
        return null;
      }
      let data = res.data;
      // Recover scores from raw JSON summary if backend parse failed (scores are 0 but JSON has real values)
      if (data?.status === 'COMPLETED' && (data.overallScore === 0 || data.overallScore == null) && data.summary) {
        try {
          let raw = data.summary;
          // Remove "Raw AI response (parse failed): " prefix
          raw = raw.replace(/^Raw AI response \(parse failed\):\s*/i, '');
          // Strip all markdown fences
          raw = raw.replace(/```[a-zA-Z]*\s*/g, '').replace(/```/g, '');
          // Extract between first { and last }
          const firstBrace = raw.indexOf('{');
          const lastBrace = raw.lastIndexOf('}');
          if (firstBrace >= 0 && lastBrace > firstBrace) {
            raw = raw.substring(firstBrace, lastBrace + 1);
          }

          let parsed;
          try {
            parsed = JSON.parse(raw);
          } catch {
            // Try repairing truncated JSON — close open brackets
            let repaired = raw.replace(/,\s*"[^"]*$/, '').replace(/,\s*$/, '');
            const stack: string[] = [];
            let inStr = false, esc = false;
            for (const c of repaired) {
              if (esc) { esc = false; continue; }
              if (c === '\\' && inStr) { esc = true; continue; }
              if (c === '"') { inStr = !inStr; continue; }
              if (inStr) continue;
              if (c === '{' || c === '[') stack.push(c);
              else if (c === '}' && stack.length && stack[stack.length-1] === '{') stack.pop();
              else if (c === ']' && stack.length && stack[stack.length-1] === '[') stack.pop();
            }
            if (inStr) repaired += '"';
            while (stack.length) { const o = stack.pop(); repaired += o === '{' ? '}' : ']'; }
            parsed = JSON.parse(repaired);
          }

          if (parsed.overall_score && parsed.scores) {
            data = {
              ...data,
              overallScore: parsed.overall_score,
              complianceScore: parsed.scores.compliance ?? 0,
              keywordScore: parsed.scores.keywords ?? 0,
              technicalTermsScore: parsed.scores.technical_terms ?? 0,
              grammarScore: parsed.scores.grammar ?? 0,
              punctuationScore: parsed.scores.punctuation ?? 0,
              structureScore: parsed.scores.structure ?? 0,
              flowScore: parsed.scores.flow ?? 0,
              wordCountScore: parsed.scores.word_count ?? 0,
              candidateFitScore: parsed.scores.candidate_fit ?? 0,
              selectionProbabilityScore: parsed.scores.selection_probability ?? 0,
              passed: parsed.overall_score >= (data.passThreshold ?? 80),
              summary: parsed.summary || data.summary,
              suggestions: parsed.suggestions || data.suggestions,
              dimensionDetails: {
                ...(data.dimensionDetails || {}),
                ...(parsed.dimension_details || {}),
                ...(parsed.per_document_scores ? { _per_document_scores: parsed.per_document_scores } : {}),
                ...(parsed.requirements_validation ? { _requirements_validation: parsed.requirements_validation } : {}),
                ...(parsed.standards_recommendations ? { _standards_recommendations: parsed.standards_recommendations } : {}),
                ...(parsed.recommendations_summary ? { _recommendations_summary: parsed.recommendations_summary } : {}),
              },
            };
          }
        } catch {
          // keep original
        }
      }
      setAnalysis(data);
      // Clear stale trigger error if a successful result has come in
      if (data?.status === 'COMPLETED') {
        setError(null);
      }
      return res.data;
    } catch {
      return null;
    }
  }, [opportunityId]);

  const fetchAllAnalyses = useCallback(async () => {
    try {
      const res = await analysisApi.getAll(opportunityId);
      setAllAnalyses(res.data ?? []);
    } catch {
      // ignore
    }
  }, [opportunityId]);

  const isRunning =
    analysis?.status === 'PENDING' || analysis?.status === 'RUNNING';
  const isCompleted = analysis?.status === 'COMPLETED';
  const isFailed = analysis?.status === 'FAILED';

  // Poll for status while an analysis is in flight; refresh history on landing.
  // The loop auto-starts/stops with `isRunning` and on unmount.
  usePolling(
    async () => {
      const data = await fetchLatest();
      if (data && (data.status === 'COMPLETED' || data.status === 'FAILED')) {
        fetchAllAnalyses();
        return false; // terminal — stop immediately
      }
    },
    POLL_INTERVAL,
    isRunning,
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLatest(), fetchAllAnalyses()]).then(() => setLoading(false));
  }, [opportunityId, fetchLatest, fetchAllAnalyses]);

  const handleTrigger = async () => {
    setTriggering(true);
    setError(null);
    try {
      const res = await analysisApi.trigger(opportunityId);
      setAnalysis(res.data);
      // Polling auto-starts when status is PENDING/RUNNING (see usePolling above).
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to start analysis';
      // If an analysis is already running/queued OR a successful result already
      // exists, show that instead of the trigger error.
      const latest = await fetchLatest();
      const latestActive = latest && (latest.status === 'PENDING' || latest.status === 'RUNNING');
      const latestDone = latest && latest.status === 'COMPLETED';
      if (!latestActive && !latestDone) {
        setError(msg);
      }
    } finally {
      setTriggering(false);
    }
  };

  const scores: { key: string; label: string; value?: number }[] = [
    { key: 'compliance', label: 'Compliance', value: analysis?.complianceScore },
    { key: 'keywords', label: 'Keywords', value: analysis?.keywordScore },
    { key: 'technical_terms', label: 'Technical Terms', value: analysis?.technicalTermsScore },
    { key: 'grammar', label: 'Grammar', value: analysis?.grammarScore },
    { key: 'punctuation', label: 'Punctuation', value: analysis?.punctuationScore },
    { key: 'structure', label: 'Structure', value: analysis?.structureScore },
    { key: 'flow', label: 'Flow', value: analysis?.flowScore },
    { key: 'word_count', label: 'Word Count', value: analysis?.wordCountScore },
    { key: 'candidate_fit', label: 'Candidate Fit', value: analysis?.candidateFitScore },
    { key: 'selection_probability', label: 'Win Probability', value: analysis?.selectionProbabilityScore },
  ];

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'ai-analysis',
          label: (
            <Space>
              <ExperimentOutlined style={{ color: 'var(--color-purple)' }} />
              <Text strong style={{ fontSize: 13 }}>
                AI Proposal Analysis
              </Text>
              {isCompleted && analysis?.passed === true && (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Passed
                </Tag>
              )}
              {isCompleted && analysis?.passed === false && (
                <Tag color="error" icon={<CloseCircleOutlined />}>
                  Re-review Recommended
                </Tag>
              )}
              {isRunning && <Tag color="processing">Analyzing...</Tag>}
            </Space>
          ),
          children: (
            <div>
              {/* Action button */}
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={isCompleted || isFailed ? <ReloadOutlined /> : <ExperimentOutlined />}
                  loading={triggering || isRunning}
                  onClick={handleTrigger}
                  disabled={isRunning}
                  style={{ background: '#722ed1', borderColor: '#722ed1' }}
                >
                  {isRunning
                    ? 'Analysis in Progress...'
                    : isCompleted || isFailed
                      ? 'Re-analyze'
                      : 'Run AI Analysis'}
                </Button>
              </div>

              {error && (
                <Alert
                  type="error"
                  message={error}
                  closable
                  onClose={() => setError(null)}
                  style={{ marginBottom: 16 }}
                />
              )}

              {loading && !analysis && <Spin tip="Loading..." />}

              {isRunning && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">
                      AI is analyzing the proposal against RFO requirements...
                    </Text>
                  </div>
                </div>
              )}

              {isFailed && analysis?.errorMessage && (
                <Alert
                  type="error"
                  message="Analysis Failed"
                  description={analysis.errorMessage}
                  style={{ marginBottom: 16 }}
                />
              )}

              {isCompleted && analysis && (
                <>
                  {/* Overall score */}
                  <Card
                    size="small"
                    style={{
                      marginBottom: 16,
                      background: analysis.passed ? 'var(--color-success-light)' : 'var(--color-warning-light)',
                      borderColor: analysis.passed ? 'var(--color-success)' : 'var(--color-warning)',
                    }}
                  >
                    <Row align="middle" gutter={24}>
                      <Col>
                        <Progress
                          type="circle"
                          percent={Math.round(analysis.overallScore ?? 0)}
                          size={80}
                          strokeColor={scoreColor(analysis.overallScore)}
                          format={(pct) => (
                            <span style={{ fontSize: 18, fontWeight: 700 }}>{pct}</span>
                          )}
                        />
                      </Col>
                      <Col flex={1}>
                        <Title level={5} style={{ margin: 0 }}>
                          Overall Score: {analysis.overallScore?.toFixed(1)}/100
                        </Title>
                        {analysis.passed === false && (
                          <Alert
                            type="warning"
                            icon={<WarningOutlined />}
                            message={`Re-review recommended \u2014 score is below ${analysis.passThreshold}%`}
                            banner
                            style={{ marginTop: 8, padding: '4px 12px' }}
                          />
                        )}
                        {analysis.passed === true && (
                          <Text type="success" strong>
                            Score meets the {analysis.passThreshold}% threshold
                          </Text>
                        )}
                      </Col>
                    </Row>
                  </Card>

                  {/* Score breakdown */}
                  <Card
                    size="small"
                    title="Score Breakdown"
                    style={{ marginBottom: 16 }}
                  >
                    <Row gutter={[16, 12]}>
                      {scores.map((s) => (
                        <Col xs={12} sm={8} md={6} lg={4} xl={4} key={s.key}>
                          <Tooltip
                            title={
                              analysis.dimensionDetails?.[s.key]
                                ? (
                                    analysis.dimensionDetails[s.key] as {
                                      findings?: string;
                                    }
                                  )?.findings
                                : undefined
                            }
                          >
                            <div style={{ textAlign: 'center' }}>
                              <Progress
                                type="dashboard"
                                percent={Math.round(s.value ?? 0)}
                                size={64}
                                strokeColor={scoreColor(s.value)}
                                format={(pct) => (
                                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                                    {pct}
                                  </span>
                                )}
                              />
                              <div>
                                <Text
                                  style={{ fontSize: 11 }}
                                  type={
                                    (s.value ?? 0) >= 80
                                      ? 'success'
                                      : (s.value ?? 0) >= 60
                                        ? 'warning'
                                        : 'danger'
                                  }
                                >
                                  {s.label}
                                </Text>
                              </div>
                            </div>
                          </Tooltip>
                        </Col>
                      ))}
                    </Row>
                  </Card>

                  {/* Suggestions */}
                  {analysis.suggestions && analysis.suggestions.length > 0 && (
                    <Card
                      size="small"
                      title="Improvement Suggestions"
                      style={{ marginBottom: 16 }}
                    >
                      {analysis.suggestions.map((s: SuggestionItem, i: number) => (
                        <div
                          key={i}
                          style={{
                            padding: '6px 0',
                            borderBottom:
                              i < (analysis.suggestions?.length ?? 0) - 1
                                ? '1px solid #f0f0f0'
                                : 'none',
                          }}
                        >
                          <Space align="start">
                            <Tag
                              color={severityColor[s.severity] || '#d9d9d9'}
                              style={{ minWidth: 60, textAlign: 'center' }}
                            >
                              {s.severity.toUpperCase()}
                            </Tag>
                            <div>
                              <Text strong style={{ fontSize: 12 }}>
                                {dimensionLabels[s.dimension] || s.dimension}
                              </Text>
                              <br />
                              <Text style={{ fontSize: 12 }}>{s.suggestion}</Text>
                            </div>
                          </Space>
                        </div>
                      ))}
                    </Card>
                  )}

                  {/* Recommendations to Improve Score */}
                  {analysis.dimensionDetails && (() => {
                    const details = analysis.dimensionDetails;
                    const recsSummary = (details as Record<string, unknown>)._recommendations_summary as string | undefined;

                    // Collect dimensions with recommendations, sorted by score ascending
                    const dimsWithRecs = Object.keys(dimensionLabels)
                      .map((key) => {
                        const dim = details[key] as { score?: number; recommendations?: string[] } | undefined;
                        return {
                          key,
                          label: dimensionLabels[key],
                          score: dim?.score ?? 0,
                          recommendations: dim?.recommendations ?? [],
                        };
                      })
                      .filter((d) => d.recommendations.length > 0)
                      .sort((a, b) => a.score - b.score);

                    if (dimsWithRecs.length === 0) return null;

                    return (
                      <Card
                        size="small"
                        title={
                          <Space>
                            <BulbOutlined style={{ color: 'var(--color-warning)' }} />
                            <span>Recommendations to Improve Score</span>
                          </Space>
                        }
                        style={{ marginBottom: 16 }}
                      >
                        {recsSummary && (
                          <Alert
                            type="info"
                            message={recsSummary}
                            style={{ marginBottom: 12 }}
                            showIcon
                          />
                        )}
                        {dimsWithRecs.map((dim) => (
                          <div
                            key={dim.key}
                            style={{
                              marginBottom: 12,
                              padding: '8px 12px',
                              background: dim.score < 60 ? 'var(--color-error-light)' : dim.score < 80 ? 'var(--color-warning-light)' : 'var(--color-success-light)',
                              borderRadius: 6,
                              border: `1px solid ${dim.score < 60 ? '#ffa39e' : dim.score < 80 ? '#ffe58f' : '#b7eb8f'}`,
                            }}
                          >
                            <div style={{ marginBottom: 4 }}>
                              <Text strong style={{ fontSize: 13 }}>
                                {dim.label}
                              </Text>
                              <Tag
                                color={scoreColor(dim.score)}
                                style={{ marginLeft: 8 }}
                              >
                                {dim.score}/100
                              </Tag>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {dim.recommendations.map((rec: string, i: number) => (
                                <li key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </Card>
                    );
                  })()}

                  {/* Standards Compliance Recommendations */}
                  {analysis.dimensionDetails && (() => {
                    const stdRecs = (analysis.dimensionDetails as Record<string, unknown>)._standards_recommendations as
                      Array<{ standard?: string; source?: string; gap?: string; recommendation?: string; priority?: string }> | undefined;
                    if (!stdRecs || stdRecs.length === 0) return null;

                    const priorityColor: Record<string, string> = {
                      critical: '#ff4d4f',
                      important: '#faad14',
                      nice_to_have: '#52c41a',
                    };
                    const priorityLabel: Record<string, string> = {
                      critical: 'CRITICAL',
                      important: 'IMPORTANT',
                      nice_to_have: 'NICE TO HAVE',
                    };

                    return (
                      <Card
                        size="small"
                        title={
                          <Space>
                            <AuditOutlined style={{ color: 'var(--color-primary)' }} />
                            <span>Standards Compliance Recommendations</span>
                            <Tag color="blue">{stdRecs.length} items</Tag>
                          </Space>
                        }
                        style={{ marginBottom: 16, borderColor: '#91d5ff' }}
                      >
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                          Actions needed to meet all RFO standards and opportunity requirements:
                        </Text>
                        {stdRecs.map((sr, i) => (
                          <div
                            key={i}
                            style={{
                              marginBottom: 10,
                              padding: '10px 12px',
                              background: sr.priority === 'critical' ? 'var(--color-error-light)' : sr.priority === 'nice_to_have' ? 'var(--color-success-light)' : 'var(--color-warning-light)',
                              borderRadius: 6,
                              border: `1px solid ${sr.priority === 'critical' ? '#ffa39e' : sr.priority === 'nice_to_have' ? '#b7eb8f' : '#ffe58f'}`,
                            }}
                          >
                            <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Tag
                                color={priorityColor[sr.priority ?? 'important'] ?? '#faad14'}
                                style={{ minWidth: 70, textAlign: 'center', fontSize: 10 }}
                              >
                                {priorityLabel[sr.priority ?? 'important'] ?? 'IMPORTANT'}
                              </Tag>
                              <Text strong style={{ fontSize: 13 }}>
                                {sr.standard}
                              </Text>
                              {sr.source && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  ({sr.source})
                                </Text>
                              )}
                            </div>
                            {sr.gap && (
                              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                                <Text type="secondary" strong style={{ fontSize: 11 }}>Gap: </Text>
                                {sr.gap}
                              </div>
                            )}
                            <div style={{ fontSize: 12, color: '#1a1a2e' }}>
                              <Text strong style={{ fontSize: 11, color: 'var(--color-primary)' }}>Action: </Text>
                              {sr.recommendation}
                            </div>
                          </div>
                        ))}
                      </Card>
                    );
                  })()}

                  {/* Per-Document Scores */}
                  {analysis.dimensionDetails && (() => {
                    const docScores = (analysis.dimensionDetails as Record<string, unknown>)._per_document_scores as
                      Array<{ document?: string; score?: number; strengths?: string; gaps?: string }> | undefined;
                    if (!docScores || docScores.length === 0) return null;

                    return (
                      <Card
                        size="small"
                        title={
                          <Space>
                            <AuditOutlined style={{ color: 'var(--color-purple)' }} />
                            <span>Per-Document Scores</span>
                            <Tag color="purple">{docScores.length} docs</Tag>
                          </Space>
                        }
                        style={{ marginBottom: 16, borderColor: '#d3adf7' }}
                      >
                        {docScores.map((ds, i) => (
                          <div
                            key={i}
                            style={{
                              marginBottom: 10,
                              padding: '10px 12px',
                              background: (ds.score ?? 0) >= 80 ? 'var(--color-success-light)' : (ds.score ?? 0) >= 50 ? 'var(--color-warning-light)' : 'var(--color-error-light)',
                              borderRadius: 6,
                              border: `1px solid ${(ds.score ?? 0) >= 80 ? '#b7eb8f' : (ds.score ?? 0) >= 50 ? '#ffe58f' : '#ffa39e'}`,
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <Text strong style={{ fontSize: 13 }}>{ds.document}</Text>
                              <Tag color={scoreColor(ds.score)}>{ds.score ?? 0}/100</Tag>
                            </div>
                            {ds.strengths && (
                              <div style={{ fontSize: 12, marginBottom: 4 }}>
                                <Text style={{ color: 'var(--color-success)', fontSize: 11 }} strong>Strengths: </Text>
                                {ds.strengths}
                              </div>
                            )}
                            {ds.gaps && (
                              <div style={{ fontSize: 12 }}>
                                <Text style={{ color: 'var(--color-error)', fontSize: 11 }} strong>Gaps: </Text>
                                {ds.gaps}
                              </div>
                            )}
                          </div>
                        ))}
                      </Card>
                    );
                  })()}

                  {/* Requirements Validation (from Gate A requirements docs) */}
                  {analysis.dimensionDetails && (() => {
                    const reqVal = (analysis.dimensionDetails as Record<string, unknown>)._requirements_validation as
                      Record<string, Array<{ requirement?: string; satisfied?: boolean; evidence?: string; gap?: string; recommendation?: string }>> | undefined;
                    if (!reqVal) return null;

                    const categories = [
                      { key: 'submission_requirements', label: 'Submission Requirements', icon: '📋', color: '#1890ff' },
                      { key: 'resource_requirements', label: 'Resource Requirements', icon: '👥', color: '#722ed1' },
                      { key: 'administrative_pass_fail', label: 'Administrative Pass/Fail', icon: '📑', color: '#fa8c16' },
                      { key: 'additional_requirements', label: 'Additional Requirements', icon: '📌', color: '#13c2c2' },
                    ].filter(c => reqVal[c.key] && reqVal[c.key].length > 0);

                    if (categories.length === 0) return null;

                    const totalReqs = categories.reduce((sum, c) => sum + (reqVal[c.key]?.length ?? 0), 0);
                    const satisfiedReqs = categories.reduce((sum, c) =>
                      sum + (reqVal[c.key]?.filter(r => r.satisfied).length ?? 0), 0);
                    const overallPct = totalReqs > 0 ? Math.round((satisfiedReqs / totalReqs) * 100) : 0;

                    return (
                      <Card
                        size="small"
                        title={
                          <Space>
                            <AuditOutlined style={{ color: 'var(--color-primary)' }} />
                            <span>Requirements Validation (Gate A)</span>
                            <Tag color={satisfiedReqs === totalReqs ? 'success' : satisfiedReqs / totalReqs >= 0.7 ? 'warning' : 'error'}>
                              {satisfiedReqs}/{totalReqs} met
                            </Tag>
                          </Space>
                        }
                        style={{ marginBottom: 16, borderColor: '#91d5ff' }}
                      >
                        {/* Overall metrics bar */}
                        <div style={{
                          background: 'var(--color-primary-light)', borderRadius: 8, padding: '16px 20px', marginBottom: 16,
                          border: '1px solid #d6e4ff',
                        }}>
                          <Row gutter={16} align="middle">
                            <Col>
                              <Progress
                                type="circle"
                                percent={overallPct}
                                size={72}
                                strokeColor={overallPct >= 80 ? '#52c41a' : overallPct >= 60 ? '#faad14' : '#ff4d4f'}
                                format={(pct) => <span style={{ fontSize: 16, fontWeight: 700 }}>{pct}%</span>}
                              />
                            </Col>
                            <Col flex={1}>
                              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                                Overall Requirements Compliance
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {satisfiedReqs} of {totalReqs} requirements from the AI-extracted RFO analysis are satisfied.
                                {totalReqs - satisfiedReqs > 0 && (
                                  <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>
                                    {' '}{totalReqs - satisfiedReqs} requirement{totalReqs - satisfiedReqs > 1 ? 's' : ''} need attention.
                                  </span>
                                )}
                              </Text>
                            </Col>
                          </Row>

                          {/* Per-category progress bars */}
                          <div style={{ marginTop: 14 }}>
                            <Row gutter={[12, 8]}>
                              {categories.map(cat => {
                                const items = reqVal[cat.key] ?? [];
                                const met = items.filter(r => r.satisfied).length;
                                const pct = items.length > 0 ? Math.round((met / items.length) * 100) : 0;
                                return (
                                  <Col xs={12} sm={6} key={cat.key}>
                                    <div style={{ textAlign: 'center' }}>
                                      <Progress
                                        type="dashboard"
                                        percent={pct}
                                        size={56}
                                        strokeColor={pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f'}
                                        format={(p) => <span style={{ fontSize: 11, fontWeight: 700 }}>{p}%</span>}
                                      />
                                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                        {cat.label.replace(' Requirements', '').replace('Administrative ', 'Admin ')}
                                      </div>
                                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                                        {met}/{items.length} met
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        </div>

                        {/* Point-by-point breakdown per category */}
                        <Collapse
                          size="small"
                          defaultActiveKey={categories.filter(c => {
                            const items = reqVal[c.key] ?? [];
                            return items.some(r => !r.satisfied);
                          }).map(c => c.key)}
                          items={categories.map(cat => {
                            const items = reqVal[cat.key] ?? [];
                            const met = items.filter(r => r.satisfied).length;
                            const unmet = items.length - met;
                            return {
                              key: cat.key,
                              label: (
                                <Space>
                                  <Text strong style={{ fontSize: 13 }}>{cat.label}</Text>
                                  <Tag color="success" style={{ fontSize: 10 }}>{met} met</Tag>
                                  {unmet > 0 && <Tag color="error" style={{ fontSize: 10 }}>{unmet} not met</Tag>}
                                </Space>
                              ),
                              children: (
                                <div>
                                  {items.map((req, i) => (
                                    <div
                                      key={i}
                                      style={{
                                        padding: '8px 12px',
                                        marginBottom: 6,
                                        borderRadius: 6,
                                        background: req.satisfied ? 'var(--color-success-light)' : 'var(--color-error-light)',
                                        border: `1px solid ${req.satisfied ? '#b7eb8f' : '#ffa39e'}`,
                                        fontSize: 12,
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                        {req.satisfied
                                          ? <CheckCircleOutlined style={{ color: 'var(--color-success)', marginTop: 2, flexShrink: 0 }} />
                                          : <CloseCircleOutlined style={{ color: 'var(--color-error)', marginTop: 2, flexShrink: 0 }} />}
                                        <div style={{ flex: 1 }}>
                                          <Text style={{ fontSize: 12, fontWeight: req.satisfied ? 400 : 600 }}>
                                            {req.requirement}
                                          </Text>
                                          {req.satisfied && req.evidence && (
                                            <div style={{ marginTop: 3 }}>
                                              <Text type="secondary" style={{ fontSize: 11 }}>
                                                <CheckCircleOutlined style={{ color: 'var(--color-success)', marginRight: 4, fontSize: 10 }} />
                                                {req.evidence}
                                              </Text>
                                            </div>
                                          )}
                                          {!req.satisfied && req.gap && (
                                            <div style={{ marginTop: 3 }}>
                                              <Text style={{ fontSize: 11, color: 'var(--color-error)' }}>
                                                <WarningOutlined style={{ marginRight: 4, fontSize: 10 }} />
                                                Gap: {req.gap}
                                              </Text>
                                            </div>
                                          )}
                                          {!req.satisfied && req.recommendation && (
                                            <div style={{
                                              marginTop: 4, padding: '4px 8px', background: 'var(--color-primary-light)',
                                              borderRadius: 4, borderLeft: '3px solid var(--color-primary)',
                                            }}>
                                              <Text style={{ fontSize: 11, color: '#003a8c' }}>
                                                <BulbOutlined style={{ marginRight: 4 }} />
                                                {req.recommendation}
                                              </Text>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ),
                            };
                          })}
                        />
                      </Card>
                    );
                  })()}

                  {/* Summary */}
                  {analysis.summary && (
                    <Card size="small" title="Summary" style={{ marginBottom: 16 }}>
                      <Paragraph style={{ margin: 0, fontSize: 13 }}>
                        {analysis.summary}
                      </Paragraph>
                    </Card>
                  )}

                  {/* Metadata */}
                  <Card size="small" style={{ marginBottom: 0, background: 'var(--color-bg-muted)' }}>
                    <Row gutter={[16, 8]}>
                      {analysis.triggeredByName && (
                        <Col xs={12} sm={8}>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Requested By</Text>
                          <Text strong style={{ fontSize: 13 }}>{analysis.triggeredByName}</Text>
                        </Col>
                      )}
                      {analysis.createdAt && (
                        <Col xs={12} sm={8}>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Date Requested</Text>
                          <Text strong style={{ fontSize: 13 }}>
                            {new Date(analysis.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                          </Text>
                        </Col>
                      )}
                      {analysis.completedAt && (
                        <Col xs={12} sm={8}>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Completed</Text>
                          <Text strong style={{ fontSize: 13 }}>
                            {new Date(analysis.completedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                          </Text>
                        </Col>
                      )}
                      {analysis.modelUsed && (
                        <Col xs={12} sm={8}>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Model</Text>
                          <Text style={{ fontSize: 12 }}>{analysis.modelUsed}</Text>
                        </Col>
                      )}
                      {analysis.tokensUsed != null && (
                        <Col xs={12} sm={8}>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Tokens Used</Text>
                          <Text style={{ fontSize: 12 }}>{analysis.tokensUsed.toLocaleString()}</Text>
                        </Col>
                      )}
                      <Col xs={12} sm={8}>
                        <Tag color="purple" style={{ marginTop: 4 }}>Saved to Content Library</Tag>
                      </Col>
                    </Row>
                  </Card>
                </>
              )}

              {/* Analysis History */}
              {allAnalyses.length > 1 && (
                <Collapse
                  size="small"
                  style={{ marginTop: 16 }}
                  items={[
                    {
                      key: 'history',
                      label: (
                        <Space>
                          <HistoryOutlined />
                          <Text style={{ fontSize: 12 }}>
                            Previous Analyses ({allAnalyses.length - 1})
                          </Text>
                        </Space>
                      ),
                      children: (
                        <div>
                          {allAnalyses.slice(1).map((prev) => (
                            <Card
                              key={prev.id}
                              size="small"
                              style={{ marginBottom: 8 }}
                            >
                              <Row align="middle" gutter={12}>
                                <Col>
                                  <Progress
                                    type="circle"
                                    percent={Math.round(prev.overallScore ?? 0)}
                                    size={44}
                                    strokeColor={scoreColor(prev.overallScore)}
                                    format={(pct) => (
                                      <span style={{ fontSize: 11, fontWeight: 700 }}>{pct}</span>
                                    )}
                                  />
                                </Col>
                                <Col>
                                  {prev.passed === true && (
                                    <Tag color="success" icon={<CheckCircleOutlined />}>Passed</Tag>
                                  )}
                                  {prev.passed === false && (
                                    <Tag color="error" icon={<CloseCircleOutlined />}>Below Threshold</Tag>
                                  )}
                                </Col>
                                <Col flex={1}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {prev.createdAt &&
                                      `Requested: ${new Date(prev.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
                                    {prev.triggeredByName &&
                                      ` · By: ${prev.triggeredByName}`}
                                    {prev.completedAt &&
                                      ` · Completed: ${new Date(prev.completedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
                                  </Text>
                                </Col>
                              </Row>
                              {prev.summary && (
                                <Paragraph
                                  style={{ margin: '8px 0 0', fontSize: 12 }}
                                  ellipsis={{ rows: 2, expandable: true }}
                                >
                                  {prev.summary}
                                </Paragraph>
                              )}
                            </Card>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default ProposalAIReview;
